<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BlogPostsAPI from 'dashboard/api/blogPosts';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import FileUploader from './FileUploader.vue';

const props = defineProps({
  post: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const activeTab = ref('content');

const formData = ref({
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  featured_image: '',
  author_name: '',
  author_avatar: '',
  category: '',
  tags: [],
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  published: false,
  position: 0,
});

const isEdit = computed(() => !!props.post);
const modalTitle = computed(() =>
  isEdit.value
    ? t('WEBSITE.BLOG.FORM.EDIT_POST')
    : t('WEBSITE.BLOG.FORM.NEW_POST')
);

const tabs = [
  {
    key: 'content',
    label: t('WEBSITE.BLOG.FORM.TABS.CONTENT'),
    icon: 'i-lucide-file-text',
  },
  {
    key: 'author',
    label: t('WEBSITE.BLOG.FORM.TABS.AUTHOR'),
    icon: 'i-lucide-user',
  },
  {
    key: 'seo',
    label: t('WEBSITE.BLOG.FORM.TABS.SEO'),
    icon: 'i-lucide-search',
  },
];

// Charger les données en mode édition
if (isEdit.value && props.post) {
  formData.value = {
    title: props.post.title || '',
    slug: props.post.slug || '',
    excerpt: props.post.excerpt || '',
    content: props.post.content || '',
    featured_image: props.post.featured_image || '',
    author_name: props.post.author_name || '',
    author_avatar: props.post.author_avatar || '',
    category: props.post.category || '',
    tags: props.post.tags || [],
    meta_title: props.post.meta_title || '',
    meta_description: props.post.meta_description || '',
    meta_keywords: props.post.meta_keywords || '',
    published: props.post.published || false,
    position: props.post.position || 0,
  };
}

// Auto-génération du slug depuis le titre
watch(
  () => formData.value.title,
  newTitle => {
    if (!isEdit.value && newTitle) {
      formData.value.slug = newTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  }
);

// Parser les tags (string séparé par virgules vers array)
const tagsInput = computed({
  get() {
    return formData.value.tags.join(', ');
  },
  set(value) {
    formData.value.tags = value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
  },
});

const handleSubmit = async () => {
  if (!formData.value.title) {
    useAlert(t('COMPONENTS.FORM.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  loading.value = true;

  try {
    if (isEdit.value) {
      await BlogPostsAPI.update(props.post.id, { blog_post: formData.value });
      useAlert(t('WEBSITE.BLOG.API.SUCCESS.POST_UPDATED'));
    } else {
      await BlogPostsAPI.create({ blog_post: formData.value });
      useAlert(t('WEBSITE.BLOG.API.SUCCESS.POST_CREATED'));
    }

    emit('save');
  } catch (error) {
    const errorMessage = isEdit.value
      ? t('WEBSITE.BLOG.API.ERROR.UPDATE_FAILED')
      : t('WEBSITE.BLOG.API.ERROR.CREATE_FAILED');
    useAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Drawer show size="lg" @close="handleClose">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ modalTitle }}
        </h2>
        <button
          class="p-2 rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x size-5 text-n-slate-11" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex gap-1 px-6 pt-4 border-b border-n-weak">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors"
          :class="[
            activeTab === tab.key
              ? 'bg-n-solid-1 text-n-blue-11 border-b-2 border-n-blue-9'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1',
          ]"
          @click="activeTab = tab.key"
        >
          <span :class="tab.icon" class="size-4" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Tab: Content -->
        <div v-show="activeTab === 'content'" class="space-y-4">
          <!-- Image à la une -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.FEATURED_IMAGE') }}
            </label>
            <FileUploader
              content-type="image"
              :current-url="formData.featured_image"
              @update:url="formData.featured_image = $event"
            />
          </div>

          <!-- Titre -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.TITLE') }}
              <span class="text-n-red-9">*</span>
            </label>
            <Input
              v-model="formData.title"
              :placeholder="t('WEBSITE.BLOG.FORM.TITLE_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Slug -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.SLUG') }}
            </label>
            <Input
              v-model="formData.slug"
              :placeholder="t('WEBSITE.BLOG.FORM.SLUG_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Catégorie et Tags -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.BLOG.FORM.CATEGORY') }}
              </label>
              <Input
                v-model="formData.category"
                :placeholder="t('WEBSITE.BLOG.FORM.CATEGORY_PLACEHOLDER')"
                class="w-full"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.BLOG.FORM.TAGS') }}
              </label>
              <Input
                v-model="tagsInput"
                :placeholder="t('WEBSITE.BLOG.FORM.TAGS_PLACEHOLDER')"
                class="w-full"
              />
            </div>
          </div>

          <!-- Extrait -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.EXCERPT') }}
            </label>
            <Textarea
              v-model="formData.excerpt"
              :rows="3"
              :placeholder="t('WEBSITE.BLOG.FORM.EXCERPT_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Contenu -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.CONTENT') }}
            </label>
            <Textarea
              v-model="formData.content"
              :rows="12"
              :placeholder="t('WEBSITE.BLOG.FORM.CONTENT_PLACEHOLDER')"
              class="w-full font-mono text-sm"
            />
          </div>

          <!-- Publié -->
          <div>
            <label class="flex items-center gap-2">
              <Checkbox v-model="formData.published" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('WEBSITE.BLOG.FORM.PUBLISHED') }}
              </span>
            </label>
          </div>
        </div>

        <!-- Tab: Author -->
        <div v-show="activeTab === 'author'" class="space-y-4">
          <!-- Avatar auteur -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.AUTHOR_AVATAR') }}
            </label>
            <FileUploader
              content-type="image"
              :current-url="formData.author_avatar"
              @update:url="formData.author_avatar = $event"
            />
          </div>

          <!-- Nom auteur -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.AUTHOR_NAME') }}
            </label>
            <Input
              v-model="formData.author_name"
              :placeholder="t('WEBSITE.BLOG.FORM.AUTHOR_NAME_PLACEHOLDER')"
              class="w-full"
            />
          </div>
        </div>

        <!-- Tab: SEO -->
        <div v-show="activeTab === 'seo'" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.META_TITLE') }}
            </label>
            <Input
              v-model="formData.meta_title"
              :placeholder="t('WEBSITE.BLOG.FORM.META_TITLE_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.META_DESCRIPTION') }}
            </label>
            <Textarea
              v-model="formData.meta_description"
              :rows="3"
              :placeholder="t('WEBSITE.BLOG.FORM.META_DESCRIPTION_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.BLOG.FORM.META_KEYWORDS') }}
            </label>
            <Input
              v-model="formData.meta_keywords"
              :placeholder="t('WEBSITE.BLOG.FORM.META_KEYWORDS_PLACEHOLDER')"
              class="w-full"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 p-6 border-t border-n-weak"
      >
        <Button
          :label="t('WEBSITE.BLOG.FORM.CANCEL')"
          color="slate"
          variant="outline"
          @click="handleClose"
        />
        <Button
          :label="t('WEBSITE.BLOG.FORM.SAVE')"
          :loading="loading"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Drawer>
</template>
